import React from "react";
import { GripVertical, MoreVertical, Pencil, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type DealStage from "@/model/interface/DealStage";

interface DealStageRowProps {
  stage: DealStage;
  dealCount: number;
  canBeDefault: boolean;
  isDragging: boolean;
  onEdit: () => void;
  onMakeDefault: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropBefore: () => void;
}

const DealStageRow: React.FC<DealStageRowProps> = ({
  stage,
  dealCount,
  canBeDefault,
  isDragging,
  onEdit,
  onMakeDefault,
  onDelete,
  onDragStart,
  onDragEnd,
  onDropBefore,
}) => {
  return (
    <li
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDropBefore();
      }}
      className={cn(
        "flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2",
        isDragging && "opacity-50",
      )}
    >
      <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground/40 active:cursor-grabbing" />
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
      <span className="flex-1 truncate text-sm font-medium">{stage.name}</span>
      {stage.isDefault && <span className="status-pill-neutral">Default</span>}
      <span className="text-xs text-muted-foreground tabular-nums">
        {dealCount} {dealCount === 1 ? "deal" : "deals"}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Options">
            <MoreVertical className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Rename and recolor
          </DropdownMenuItem>
          {canBeDefault && !stage.isDefault && (
            <DropdownMenuItem onClick={onMakeDefault}>
              <Star className="mr-2 h-4 w-4" />
              Set as default
            </DropdownMenuItem>
          )}
          {!stage.isDefault && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove stage
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
};

export default DealStageRow;
